import React, { useCallback, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  TextField,
  Typography,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import { FormattedHTMLMessage, FormattedMessage } from "react-intl";
import {
  changeRecommenderAdmin,
  getUsersBelow,
  showErrorMessage,
  showSuccessMessage,
} from "../../actions";
import AbtcAvatar from "../../custom/Avatar";
import {
  capitalCaseNormalizer,
  debounce,
  resolveErrorMessage,
} from "../../util";
import InfiniteScrollAsync from "./InfiniteScrollAsync";
import { useDispatch } from "react-redux";
import LocaleNumberField from "./LocaleNumberField";
import LoadingSpinner from "./LoadingSpinner";

const DEFAULT_RECOMMENDER_PERCENT= 0.3

const FormChangeRecommender = ({
  open,
  onClose,
  onSuccess,
  user,
  currentUser,
  includeMe,
  hierarchyViewType,
  actionEditRecommender={changeRecommenderAdmin}
}) => {
  const currentRecommenderPercent = user?.recommender?.percent ?? user?.recommenderPercent ?? DEFAULT_RECOMMENDER_PERCENT;
  const scrollContainerRef = useRef(null);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [recommenderPercent, setRecommenderPercent] = useState(currentRecommenderPercent * 100);
  const [newRecommender, setNewRecommender] = useState(null);
  const [search, setSearch] = useState({
    recommenderId: "",
    recommenderName: "",
  });

  const currentUserMatchesSearch = (
    { recommenderId, recommenderName },
    currentUser,
  ) => {
    if (recommenderId && currentUser.id !== recommenderId) return false;

    if (
      recommenderName &&
      !currentUser.name.toLowerCase().includes(recommenderName.toLowerCase())
    ) {
      return false;
    }

    return true;
  };

  const fetchRecommendersPaginate = useCallback(async ({ params, skip = 0, take = 10 }) => {
    const paramsWithPaging = {
      userId: params.recommenderId,
      nameOrEmail: params.recommenderName,
      skip,
      take,
      hierarchyViewType,
    };
    return getUsersBelow(paramsWithPaging);
  }, [hierarchyViewType]);

  const shouldFilter = useCallback(
    (recommenders, params) => {
      const recommendersWithoutUsers = recommenders.filter((recommender) => recommender.id !== user.consultant?.id);

      if (!currentUserMatchesSearch(params, currentUser)) return recommendersWithoutUsers;
      if (recommendersWithoutUsers.some((r) => r.id === currentUser.id)) return recommendersWithoutUsers;
      return [...(includeMe ? [currentUser] : []), ...recommendersWithoutUsers];
    },
    [user, currentUser, includeMe]
  );

  const handleSearchId = useCallback(
    debounce((value) => {
      setSearch({
        recommenderId: value,
        recommenderName: "",
      });
    }),
    []
  );
  const handleSearchName = useCallback(
    debounce((value) => {
      setSearch({
        recommenderId: "",
        recommenderName: value,
      });
    }),
    []
  );

  const handleConfirm = async () => {
    if (!newRecommender) {
      return;
    }
    try {
      const isAddingRecommender = !user?.recommender;
      const data = {
        recommenderId: newRecommender.id,
        recommenderPercent: recommenderPercent / 100,
      };
      setLoading(true);
      await actionEditRecommender(user.id, data);
      dispatch(
        showSuccessMessage(
          isAddingRecommender
            ? "app.recommender.add.success"
            : "app.recommender.change.success"
        )
      );
      await onSuccess();
      handleClose(false);
    } catch (e) {
      const errorMessage = resolveErrorMessage(e);
      dispatch(showErrorMessage(errorMessage));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (resetPercentage = true) => {
    onClose();
    setSearch({
      recommenderId: "",
      recommenderName: "",
    });
    setNewRecommender(null);
    if (resetPercentage) {
      setRecommenderPercent(currentRecommenderPercent * 100);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>
        <Typography
          variant="h6"
          component="span"
        >
          {user?.recommender ? <FormattedMessage id="app.changeRecommender" /> : <FormattedMessage id="app.addRecommender" />}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography
          variant="body1"
          style={{ marginBottom: 16 }}
        >
          <FormattedHTMLMessage id="app.changeRecommender.text" />
        </Typography>

        <Divider />
        <br />
        <Grid
          container
          spacing={1}
        >
          <Grid
            item
            xs={12}
          >
            {newRecommender ? (
              <>
                <Typography
                  variant="subtitle1"
                  color="primary"
                >
                  <FormattedMessage id="app.user.selected" />
                </Typography>
                <ListItem alignItems="flex-start">
                  <ListItemAvatar>
                    <AbtcAvatar
                      imageUrl={newRecommender.photoUrl}
                      disableLink
                      enablePremiumBorder={newRecommender.premium}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span>{`${capitalCaseNormalizer(newRecommender.name)} (${newRecommender.id})`}</span>
                      </div>
                    }
                    secondary={
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span>{newRecommender.email}</span>
                      </div>
                    }
                  />
                </ListItem>
              </>
            ) : (
              <Typography
                paragraph
                variant="body1"
                color="primary"
              >
                <FormattedMessage id="app.user.select" />
              </Typography>
            )}
          </Grid>

          <Grid
            item
            xs={12}
          >
            <Grid
              container
              spacing={1}
            >
              <Grid
                item
                xs={8}
              >
                <LocaleNumberField
                  disabled={loading}
                  endAdornment="%"
                  min={0}
                  max={100}
                  value={recommenderPercent}
                  setValue={setRecommenderPercent}
                  fullWidth
                />
              </Grid>
              <Grid
                item
                xs={4}
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleConfirm}
                  disabled={loading || recommenderPercent === "" || recommenderPercent === 0 || !newRecommender}
                  fullWidth
                >
                  <FormattedMessage id="app.ok" />
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {loading && <LoadingSpinner message={<FormattedMessage id="app.loading.updating" />} /> }
        
        <>
          <br />
          <Divider />
          <br />
          <Grid
            container
            spacing={1}
          >
            <Grid
              item
              xs={12}
              sm={8}
            >
              <TextField
                variant="outlined"
                onChange={(e) => handleSearchName(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                size="small"
                label={<FormattedMessage id="app.searchRecommender" />}
                disabled={loading}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>

            <Grid
              item
              xs={12}
              sm={4}
            >
              <TextField
                variant="outlined"
                type="number"
                label="Id"
                size="small"
                InputLabelProps={{ shrink: true }}
                disabled={loading}
                onChange={(e) => handleSearchId(e.target.value)}
                fullWidth
              />
            </Grid>
          </Grid>

          <div
            ref={scrollContainerRef}
            style={{
              height: 400,
              overflowY: "auto",
            }}
          >
            <List disablePadding>
              {open && (
                <InfiniteScrollAsync
                  emptyMessage={"app.users.not.filter"}
                  offsetIncrement={50}
                  onScroll={fetchRecommendersPaginate}
                  params={search}
                  take={20}
                  scrollContainerRef={scrollContainerRef}
                  shouldFilter={shouldFilter}
                  render={(r) => (
                    <ListItem
                      button
                      alignItems="flex-start"
                      key={r.id}
                      selected={newRecommender?.id === r.id}
                      onClick={() => {
                        if (loading) return;
                        setNewRecommender(r);
                      }}
                    >
                      <ListItemAvatar>
                        <AbtcAvatar
                          imageUrl={r.photoUrl}
                          disableLink
                        />
                      </ListItemAvatar>

                      <ListItemText
                        primaryTypographyProps={{ component: "div" }}
                        secondaryTypographyProps={{ component: "div" }}
                        primary={
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <span>{capitalCaseNormalizer(r.name)}</span>
                            <span>{"Id: " + r.id}</span>
                          </div>
                        }
                        secondary={
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <span>{r.email}</span>
                          </div>
                        }
                      />
                    </ListItem>
                  )}
                />
              )}
            </List>
          </div>
        </>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="primary" fullWidth onClick={handleClose}>
          <FormattedMessage id="app.back" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FormChangeRecommender;
